import {
    Control,
    FieldErrors,
    useForm,
    UseFormRegister,
    UseFormReset,
    UseFormSetValue,
    UseFormWatch
} from "react-hook-form";
import React, {ReactNode, useEffect, useState} from "react";
import {Form} from "react-bootstrap";
import {Identifiable} from "../model/interface/Identifiable";
import useDatasource from "../hooks/useDatasource";
import {Normalizer} from "../services/normalizer/normalizer";
import Spinner from "../components/Spinner";
import {SubmitHandler} from "react-hook-form/dist/types/form";
import axios from "axios";

export type renderFormProps<ResourceT extends Identifiable> = {
    register: UseFormRegister<any>
    control: Control<any>
    errors: FieldErrors
    reset: UseFormReset<any>
    resource: ResourceT
    watch: UseFormWatch<any>
    setValue: UseFormSetValue<any>
    isLoading: boolean,
    getValues: any
}

type FormControllerProps<ResourceT extends Identifiable> = {
    renderForm: (props: renderFormProps<ResourceT>) => ReactNode
    resetOnSubmit?: boolean,
    handleSubmitEvent?: (previousResource: ResourceT) => SubmitHandler<any>,
    namespace: string,
    normalizer: Normalizer<ResourceT>
    id?: Identifiable["id"] | null
    newResourceCreator?: () => ResourceT
}


export function FormController<ResourceT extends Identifiable>({renderForm, resetOnSubmit = false, handleSubmitEvent, namespace, normalizer, id, newResourceCreator}: FormControllerProps<ResourceT>) {

    const [isLoading, setIsLoading] = useState(true);
    const [resource, setResource] = useState<ResourceT | null>(null);

    const {get} = useDatasource<ResourceT>(namespace, normalizer);

    const methods = useForm<ResourceT>({});
    const {
        handleSubmit,
        register,
        control,
        formState: {errors},
        reset,
        watch,
        setValue,
        getValues,
    } = methods;

    useEffect(() => {
        const controller = new AbortController();

        if (id) {
            get(id, controller).then(el => {
                setResource(el);
                setIsLoading(false);
                reset(el);
            }).catch(err => {
                if (axios.isCancel(err)) {
                    return;
                }
                throw err;
            });
        } else {
            setIsLoading(false)
            if (newResourceCreator) {
                setResource(newResourceCreator())
            } else {
                console.warn(`missing newResourceCreator for FormController ${namespace}`)
            }
        }

        return () => {
            controller.abort();
        }
    }, [get, id, reset, newResourceCreator, namespace])

    if (isLoading || !resource) {
        return <Spinner/>
    }

    const formFields = renderForm({register, control, errors, reset, resource, watch, setValue, isLoading, getValues});

    return <Form onSubmit={handleSubmit((data, event) => {
        if (handleSubmitEvent) {
            handleSubmitEvent(resource)(data, event)
        }

        if (resetOnSubmit) {
            reset();
        }
    })}>
        {formFields}
    </Form>
}
